import React, { useState } from 'react'
import { useCreateBookingMutation, useUpdateBookingMutation } from '../Service/bookingsApi'

import { bookingValues } from '../initialValues';
import { bookingFields } from '../fields';
import { BookingsRoutes } from '../../../Router/path';
import { OffersRoutes } from '../../../Router/path';
import { ReservationsRoutes } from '../../../Router/path';
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FormRenderGroupTypes } from '../../Shared/Components/FormRenderer/FormRenderGroup.types';
import { FormRenderTypes } from '../../Shared/Components/FormRenderer';
import EntityFormLayout from '../../Shared/Components/EntityFormLayout/EntityFormLayout';
import { useGetOffersQuery } from '../../Offers/Service/offersApi';
import { useGetDiscountsQuery } from '../../Discounts/Service/discountsApi';
import AccordionCard from '../../Shared/Components/AccordionCard/AccordionCard';
import { Alert, Button, Typography } from '@mui/material';
import { MultiAssign } from '../../Shared/Components/MultiAssign';
import { useAssignDiscountsToBookingMutation } from '../Service/extendedBookingsApi';
import { Booking } from '../../Shared/Types/bookingTypes';
import { useEntityForm } from '../../Shared/Hooks/useEntityForm';
import { ControlledSubmitError } from '../../Shared/Hooks/useSubmitForm';
import { useLocation, useNavigate } from 'react-router-dom';
import { useInitialBookingValues } from './useInitialBookingValues';
import {
  parseBankDocumentReservationCreateQuery,
} from '../../Shared/Helpers/bankDocumentReservationCreateNavigation';

interface BookingReturnContext {
  pathname: string;
  search?: string;
  label?: string;
}

interface BookingNavigationState {
  returnContext?: BookingReturnContext;
}

const CreateBooking = () => {
  const headerIcon = <BookOnlineIcon fontSize="large" color="primary" />;
  const sectionInfoIcon = <InfoIcon fontSize="small" color="primary" />;
  const sectionSettingsIcon = <SettingsIcon fontSize="small" color="primary" />;

  const [createBooking, statusCreate] = useCreateBookingMutation();
  const [updateBooking, statusUpdate] = useUpdateBookingMutation();
  const [assign, { isLoading: isAssigning }] = useAssignDiscountsToBookingMutation();

  const { data: offers, isLoading: offersLoading } = useGetOffersQuery();
  const { data: allDiscounts, isLoading: allDiscountsLoading } = useGetDiscountsQuery();
  const [createdBookingId, setCreatedBookingId] = useState<number | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [clientValidationError, setClientValidationError] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();
  const navigationState = (location.state as BookingNavigationState | null) ?? null;
  const returnContext =
    navigationState?.returnContext?.pathname === OffersRoutes.root
      ? navigationState.returnContext
      : null;
  const bankDocumentReservationCreateContext = parseBankDocumentReservationCreateQuery(
    location.search
  );
  const redirectUrl = bankDocumentReservationCreateContext ? undefined : BookingsRoutes.root;
  const backUrl = bankDocumentReservationCreateContext?.returnTo ?? BookingsRoutes.root;
  const bankDocumentRedirectDelay = 2000;

  const initialBookingValues: Booking = useInitialBookingValues(location.search);

  const validateBeforeSubmit = (data: Booking): string | null => {
    const parsedOfferId = Number(data.offerId);
    if (!Number.isFinite(parsedOfferId) || parsedOfferId <= 0) {
      return 'Oferta jest wymagana.';
    }

    const parsedPrice = Number(data.price);
    if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
      return 'Cena musi być większa od zera.';
    }

    const isPublished = data.published === true;
    if (isPublished) {
      const parsedParticipants = Number(data.numberOfParticipants);
      if (!Number.isInteger(parsedParticipants) || parsedParticipants <= 0) {
        return 'Liczba uczestników musi być większa od zera dla opublikowanej rezerwacji.';
      }
    }

    return null;
  };

  const handleAddReservation = () => {
    if (!createdBookingId) {
      return;
    }

    const returnTo = BookingsRoutes.update.replace(':bookingId', String(createdBookingId));
    navigate(
      `${ReservationsRoutes.create}?bookingId=${createdBookingId}&returnTo=${encodeURIComponent(returnTo)}`
    );
  };

  const entityForm = useEntityForm<Booking>({
    initialData: initialBookingValues,
    fields: bookingFields.map((field) => {
      if (field.name === 'offerId' && field.type === FormRenderTypes.autocomplete && offers) {
        return {
          ...field,
          name: field.name as keyof Booking,
          options: offers.map((offer) => ({
            label: offer.name,
            value: offer.id,
          })),
          loading: offersLoading,
          value: initialBookingValues.offerId,
        };
      }

      if (field.name === 'bookingStatusName') {
        const options = [{ label: 'Potwierdzona', value: 'Confirmed' }];
        return {
          ...field,
          name: 'bookingStatusName',
          label: field.label,
          options,
        };
      }

      return {
        ...field,
        name: field.name as keyof Booking,
        value: bookingValues[field.name as keyof Booking],
      };
    }),
    redirectUrl,
    submitFn: (data) => {
      const normalizedData: Booking = {
        ...data,
        bookingStatusName: data.bookingStatusName?.trim() ? data.bookingStatusName : 'Confirmed',
      };

      const validationError = validateBeforeSubmit(normalizedData);
      if (validationError) {
        setClientValidationError(validationError);
        throw new ControlledSubmitError(validationError);
      }

      setClientValidationError('');

      if (createdBookingId) {
        return updateBooking({ data: normalizedData, id: createdBookingId }).unwrap();
      } else {
        const { id, ...payload } = normalizedData;
        return createBooking(payload).unwrap();
      }
    },
    mutationStatus: createdBookingId ? statusUpdate : statusCreate,
    title: 'Dodaj rezerwację',
    icon: headerIcon,
    backUrl,
    headerActions: returnContext ? (
      <Button
        color="primary"
        size="small"
        startIcon={<ArrowBackIcon fontSize="small" />}
        onClick={() => navigate(`${returnContext.pathname}${returnContext.search ?? ''}`)}
      >
        {returnContext.label ?? 'Wróć do listy ofert'}
      </Button>
    ) : undefined,
    groupOrder: [FormRenderGroupTypes.basic, FormRenderGroupTypes.configuration],
    groupIcons: {
      Podstawowe: sectionInfoIcon,
      Konfiguracja: sectionSettingsIcon,
    },
    showSuccessBeforeRedirect: true,
    redirectDelay: bankDocumentRedirectDelay,
    onSuccessWithResponse: (response, shouldRedirect) => {
      const createdBookingIdFromResponse =
        typeof response?.bookingId === 'number' && Number.isInteger(response.bookingId) && response.bookingId > 0
          ? response.bookingId
          : null;

      if (!shouldRedirect && !createdBookingId && createdBookingIdFromResponse) {
        setCreatedBookingId(createdBookingIdFromResponse);
      }
    },
  });

  return (
    <EntityFormLayout {...entityForm}>
      {clientValidationError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {clientValidationError}
        </Alert>
      )}
      <AccordionCard title="Przypisz zniżki do rezerwacji" icon={headerIcon}>
        {createdBookingId ? (
          <MultiAssign
            options={allDiscounts || []}
            loadingOptions={allDiscountsLoading}
            selectedIds={selected}
            onChange={setSelected}
            getOptionLabel={(d) => d.name}
            getOptionValue={(d) => d.id}
            onSave={async () => {
              await assign({ bookingId: createdBookingId, discountIds: selected }).unwrap();
            }}
            saving={isAssigning}
            label="Wybierz zniżki"
            placeholder="Zacznij pisać…"
          />
        ) : (
          <Typography color="text.secondary">
            Zniżki możesz dodać dopiero po zapisaniu rezerwacji.
          </Typography>
        )}
      </AccordionCard>
      <AccordionCard title="Dodaj rezerwację do bookingu" icon={headerIcon}>
        {createdBookingId ? (
          <Button variant="contained" onClick={handleAddReservation}>
            Dodaj nową rezerwację
          </Button>
        ) : (
          <Typography color="text.secondary">
            Rezerwację możesz dodać dopiero po zapisaniu bookingu.
          </Typography>
        )}
      </AccordionCard>
    </EntityFormLayout>
  );
};

export default CreateBooking


